import { useEffect, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { useI18n } from '@/i18n/useI18n';
import { cn } from '@/ui/cn';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

/**
 * Marco de la aplicación: cabecera, navegación y contenido.
 *
 * Accesibilidad que ya está resuelta y que ningún módulo tiene que repetir:
 *
 * - **Enlace de salto** al contenido. Sin él, cada navegación con teclado
 *   arranca recorriendo los nueve enlaces del menú.
 * - `<main id="content" tabindex="-1">` con `key` en la ruta: al cambiar de
 *   módulo el foco vuelve al contenido en vez de quedarse donde estaba.
 * - Landmarks (`header`, `nav`, `main`) para que un lector de pantalla pueda
 *   saltar entre regiones.
 */
export function AppShell(): ReactNode {
  const { t } = useI18n();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // El cajón se cierra con Escape y bloquea el scroll del fondo mientras está
  // abierto: es lo que hace cualquier menú lateral de móvil, y sin lo segundo
  // el contenido se desplaza detrás del cajón y el gesto de cerrar no se
  // distingue del de scrollear.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [navOpen]);

  return (
    <div className="min-h-screen bg-surface-sunken">
      <a
        href="#content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-brand focus:px-3 focus:py-2 focus:text-brand-contrast"
      >
        {t('app.skipToContent')}
      </a>

      <TopBar
        navOpen={navOpen}
        onToggleNav={() => {
          setNavOpen((open) => !open);
        }}
      />

      <div className="mx-auto flex w-full max-w-[110rem]">
        {/* Debajo de `lg` el menú es un cajón que se superpone al contenido
            (off-canvas), no una columna que lo aplasta: en un teléfono, 240px
            de menú al lado del contenido dejaban 120px para la tabla. El fondo
            oscurecido cierra al tocarlo. */}
        {navOpen && (
          <button
            type="button"
            aria-label={t('nav.close')}
            onClick={() => {
              setNavOpen(false);
            }}
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          />
        )}
        <aside
          id="app-nav"
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-60 shrink-0 bg-sidebar shadow-xl transition-transform duration-200 ease-out',
            'lg:static lg:z-auto lg:translate-x-0 lg:shadow-none lg:transition-none',
            navOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex h-full flex-col overflow-y-auto lg:sticky lg:top-0 lg:max-h-screen lg:min-h-screen">
            <Sidebar
              onNavigate={() => {
                setNavOpen(false);
              }}
            />
          </div>
        </aside>

        <main
          id="content"
          key={location.pathname}
          tabIndex={-1}
          className="min-w-0 flex-1 p-4 lg:p-6"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
